/**
 * A target can exist without a plan. The optional plan id links this same row.
 * Blank hierarchy fields mean All for that axis.
 */

import React, { useState } from 'react';
import api from '@/services/api';
import { Button } from '@/components/ui/Button';
import { useLanguage } from '@/state/language/LanguageProvider';
import toast from 'react-hot-toast';

export const KpiTargetEntry: React.FC<{ kpiId: string }> = ({ kpiId }) => {
  const { t } = useLanguage();
  const [useType, setUseType] = useState('OPERATIONAL');
  const [value, setValue] = useState('');
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState('');
  const [planId, setPlanId] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    try {
      setBusy(true);
      await api.post('/kpis/targets', {
        kpi_id: kpiId,
        use_type: useType,
        target_value: Number(value),
        period_from: periodFrom || null,
        period_to: periodTo || null,
        plan_id: planId || null
      });
      toast.success(t('kpi.targetSaved'));
      setValue('');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t('kpi.targetSaveFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-4 grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
      <div>
        <label className="block text-xs text-[var(--color-secondary-text)] mb-1">{t('kpi.useType')}</label>
        <select className="w-full border border-[var(--color-border)] rounded px-2 py-1 text-sm" value={useType} onChange={(event) => setUseType(event.target.value)}>
          <option value="STRATEGIC">{t('kpi.useStrategic')}</option>
          <option value="ANNUAL">{t('kpi.useAnnual')}</option>
          <option value="OPERATIONAL">{t('kpi.useOperational')}</option>
          <option value="PROJECT">{t('kpi.useProject')}</option>
        </select>
      </div>
      <div>
        <label className="block text-xs text-[var(--color-secondary-text)] mb-1">{t('kpi.target')}</label>
        <input className="w-full border border-[var(--color-border)] rounded px-2 py-1 text-sm" value={value} onChange={(event) => setValue(event.target.value)} />
      </div>
      <div>
        <label className="block text-xs text-[var(--color-secondary-text)] mb-1">{t('kpi.periodFrom')}</label>
        <input type="date" className="w-full border border-[var(--color-border)] rounded px-2 py-1 text-sm" value={periodFrom} onChange={(event) => setPeriodFrom(event.target.value)} />
      </div>
      <div>
        <label className="block text-xs text-[var(--color-secondary-text)] mb-1">{t('kpi.periodTo')}</label>
        <input type="date" className="w-full border border-[var(--color-border)] rounded px-2 py-1 text-sm" value={periodTo} onChange={(event) => setPeriodTo(event.target.value)} />
      </div>
      <div className="md:col-span-2">
        <label className="block text-xs text-[var(--color-secondary-text)] mb-1">{t('kpi.optionalPlan')}</label>
        <input className="w-full border border-[var(--color-border)] rounded px-2 py-1 text-sm" value={planId} onChange={(event) => setPlanId(event.target.value)} placeholder={t('kpi.optionalPlanHint')} />
      </div>
      <Button variant="primary" onClick={save} disabled={busy || value === ''}>{t('kpi.saveTarget')}</Button>
    </div>
  );
};
